/**
 * Semrush Integration Module (Story 20.4)
 *
 * Barrel export + side-effect adapter registration.
 *
 * Registration happens here — NOT in `semrush.ts` — to prevent the
 * Failure 5a anti-pattern documented in `google/index.ts:7`. Importing this
 * module is sufficient to register the adapter; importing `semrush.ts`
 * directly would give you the adapter object without registration.
 */

import { registerAdapter } from "../registry";
import { semrushAdapter } from "./semrush";

// Side-effect registration (Story 20.4)
registerAdapter("semrush", semrushAdapter);

// Public surface — adapter + canonical constants
export {
  semrushAdapter,
  SEMRUSH_PLATFORM_VALUES,
  SEMRUSH_METRICS,
  SEMRUSH_DIMENSIONS,
  SEMRUSH_API_VERSION,
  SEMRUSH_ANALYTICS_API_BASE,
  SEMRUSH_BACKLINKS_API_BASE,
  SEMRUSH_TRENDS_API_BASE,
  SEMRUSH_BALANCE_URL,
  SEMRUSH_SYNTHETIC_METRIC_KEYS,
  SEMRUSH_DOMAIN_RANKS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_OVERVIEW_DEFAULT_COLUMNS,
  SEMRUSH_PHRASE_KDI_DEFAULT_COLUMNS,
  SEMRUSH_TRENDS_SUMMARY_DEFAULT_COLUMNS,
  // Story 20.5 — keyword-rank default-columns constants.
  SEMRUSH_DOMAIN_ORGANIC_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_ADWORDS_DEFAULT_COLUMNS,
  SEMRUSH_PHRASE_EXPANSION_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_RANK_HISTORY_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_COLUMNS,
  // Story 35.1 — Backlinks expansion + URL Reports default-columns constants.
  SEMRUSH_BACKLINKS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_REFDOMAINS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_ANCHORS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_PAGES_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_TLD_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_GEO_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_REFIPS_DEFAULT_COLUMNS,
  SEMRUSH_URL_ORGANIC_DEFAULT_COLUMNS,
  SEMRUSH_URL_ADWORDS_DEFAULT_COLUMNS,
  validateSemrushConnection,
  normalizeSemrushTarget,
  transformSemrushCurrencyField,
  previousFirstOfMonthIso,
  // Story 20.9 — cross-SEO snapshot demo constants
  SEMRUSH_DEMO_SNAPSHOT_TARGET,
  SEMRUSH_DEMO_SNAPSHOT_ROW_COUNT,
  type SemrushPlatform,
} from "./semrush";

// API helpers
export {
  fetchSemrushDomainRanks,
  fetchSemrushBacklinksOverview,
  fetchSemrushPhraseKdi,
  fetchSemrushTrendsSummary,
  type SemrushDomainRanksParams,
  type SemrushBacklinksOverviewParams,
  type SemrushPhraseKdiParams,
  type SemrushTrendsSummaryParams,
  type SemrushBalanceResult,
  type SemrushHelperResult,
  // Story 20.5 — 6 new helpers + Params types + cost constants + phraseHash.
  fetchSemrushDomainOrganic,
  fetchSemrushDomainAdwords,
  fetchSemrushPhraseRelated,
  fetchSemrushPhraseQuestions,
  fetchSemrushDomainRankHistory,
  fetchSemrushDomainOrganicOrganic,
  computeSemrushPhraseHash,
  SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_LIMIT,
  SEMRUSH_DOMAIN_RANK_HISTORY_MAX_MONTHS,
  SEMRUSH_DOMAIN_RANK_HISTORY_MAX_DAYS,
  type SemrushDomainOrganicParams,
  type SemrushDomainAdwordsParams,
  type SemrushPhraseExpansionParams,
  type SemrushDomainRankHistoryParams,
  type SemrushDomainOrganicCompetitorsParams,
  type SemrushHelperResultWithWarnings,
  // Story 35.1 — 9 new helpers (7 backlinks + 2 url reports) + Params types
  // + fixed-cost constant + url-report default-limit clamp.
  fetchSemrushBacklinks,
  fetchSemrushBacklinksRefdomains,
  fetchSemrushBacklinksAnchors,
  fetchSemrushBacklinksPages,
  fetchSemrushBacklinksTld,
  fetchSemrushBacklinksGeo,
  fetchSemrushBacklinksRefips,
  fetchSemrushUrlOrganic,
  fetchSemrushUrlAdwords,
  SEMRUSH_BACKLINKS_UNIT_COST,
  SEMRUSH_BACKLINKS_DEFAULT_LIMIT,
  SEMRUSH_URL_REPORTS_DEFAULT_LIMIT,
  type SemrushBacklinksParams,
  type SemrushBacklinksRefdomainsParams,
  type SemrushBacklinksAggregateParams,
  type SemrushUrlReportParams,
} from "./api";

// Story 20.5 — cache policy + per-endpoint TTL map + canonical-JSON-hash.
export {
  SEMRUSH_ENDPOINT_CACHE_TTL,
  SEMRUSH_ENDPOINT_TO_ROUTE_PATH,
  getSemrushCacheTtl,
  buildSemrushEndpointCacheKey,
  type SemrushEndpoint,
} from "./cache-policy";

// CSV parser
export {
  parseSemrushCsv,
  type SemrushCsvResult,
} from "./csv";

// Errors
export {
  parseSemrushApiError,
  semrushErrorTypeToAdapterCode,
  sanitizeSemrushErrorMessage,
  SEMRUSH_PLAINTEXT_ERROR_REGEX,
  SemrushApiCallError,
  type SemrushApiError,
  type SemrushErrorType,
} from "./errors";

// Normalize
export {
  SemrushTargetValidationError,
  // Story 35.1 — URL normalizer (preserves scheme + path + query, distinct
  // from `normalizeSemrushTarget` which strips the scheme).
  normalizeSemrushUrl,
} from "./normalize";

// Safe fetch
export {
  safeFetchSemrush,
  redactSemrushUrlKey,
} from "./safe-fetch";

// Transformers
export {
  transformDomainRanksRow,
  transformBacklinksOverviewRow,
  transformPhraseKdiRow,
  transformTrendsSummaryRow,
  transformSemrushIntegerField,
  type SemrushReportType,
  // Story 20.5 — 6 new transformers + SERP feature parser + CSV-injection escape.
  transformDomainOrganicRow,
  transformDomainAdwordsRow,
  transformPhraseRelatedRow,
  transformPhraseQuestionsRow,
  transformDomainRankHistoryRow,
  transformDomainOrganicCompetitorsRow,
  parseSemrushSerpFeatures,
  escapeCsvFieldForExport,
  // Story 35.1 — 9 new transformers (7 backlinks + 2 url reports).
  transformBacklinksRow,
  transformBacklinksRefdomainsRow,
  transformBacklinksAnchorsRow,
  transformBacklinksPagesRow,
  transformBacklinksTldRow,
  transformBacklinksGeoRow,
  transformBacklinksRefipsRow,
  transformUrlOrganicRow,
  transformUrlAdwordsRow,
} from "./transformers";

// Story 20.5 — Vitest mock helper for keyword-rank route tests.
export {
  mockSemrushKeywordResponse,
  // Story 35.1 — Vitest mock helpers for Backlinks + URL Report route tests.
  mockSemrushBacklinksResponse,
  mockSemrushUrlReportResponse,
} from "./__mocks__/keyword-fixtures";
export type {
  MockSemrushKeywordResponseOptions,
  MockSemrushBacklinksResponseOptions,
  MockSemrushUrlReportResponseOptions,
} from "./__mocks__/keyword-fixtures";

// Story 20.6 — Renderer-side aggregate / filter / gating helpers.
export {
  filterRecordsByReportType,
  computeSemrushAggregates,
  resolveSemrushCatalogueKey,
  applyTrendsApiGate,
  readTrendsEntitled,
  type SemrushSyntheticAggregates,
  type TrendsApiGateOutcome,
} from "./aggregates";

// Unit budget tracking
export {
  SEMRUSH_DAILY_TENANT_UNIT_BUDGET,
  SEMRUSH_MONTHLY_TRENDS_UNIT_BUDGET,
  SEMRUSH_UNITS_KV_TTL_SEC,
  SEMRUSH_DISPLAY_LIMIT_MAX,
  buildSemrushTenantUnitsKey,
  buildSemrushIntegrationUnitsKey,
  buildSemrushTrendsUnitsKey,
  buildSemrushTenantBreachMarkerKey,
  todayUtcYyyyMmDd,
  thisMonthUtcYyyyMm,
  incrementSemrushUnitsUsed,
  incrementSemrushTrendsUnitsUsed,
  getSemrushTenantDailyUnits,
  getSemrushIntegrationDailyUnits,
  getSemrushTrendsMonthlyUnits,
  checkSemrushUnitBudget,
  checkSemrushTrendsUnitBudget,
  type SemrushBudgetCheckResult,
} from "./unit-budget";
